import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { trackPageView } from "@/lib/meta";
import { AuthProvider } from "@/context/AuthContext";
import { MemberAuthProvider } from "@/context/MemberAuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ProtectedRoute from "@/components/ProtectedRoute";
import MemberAccount from "@/pages/membership/MemberAccount";
import MemberAuthCallback from "@/pages/membership/MemberAuthCallback";
import LastHorizonApply from "@/pages/lasthorizon/Apply";
import LastHorizonTerms from "@/pages/lasthorizon/Terms";
import DawnApply from "@/pages/dawn/DawnApply";
import DawnTerms from "@/pages/dawn/Terms";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Focus from "@/pages/Focus";
import Programmes from "@/pages/Programmes";
import ProgrammeDetail from "@/pages/ProgrammeDetail";
import WhereWeWork from "@/pages/WhereWeWork";
import Impact from "@/pages/Impact";
import Partnerships from "@/pages/Partnerships";
import Stories from "@/pages/Stories";
import StoryDetail from "@/pages/StoryDetail";
import Exploration from "@/pages/Exploration";
import Expeditions from "@/pages/Expeditions";
import Films, { FilmDetail } from "@/pages/Films";
import JourneyDetail from "@/pages/JourneyDetail";
import Flagship from "@/pages/Flagship";
import Learning from "@/pages/Learning";
import TrainingCatalogue from "@/pages/learning/TrainingCatalogue";
import TrainingDetail from "@/pages/learning/TrainingDetail";
import Pathways from "@/pages/learning/Pathways";
import FieldExperiences from "@/pages/learning/FieldExperiences";
import Upcoming from "@/pages/learning/Upcoming";
import Institutional from "@/pages/learning/Institutional";
import Verify from "@/pages/Verify";
import Donate from "@/pages/Donate";
import Volunteer from "@/pages/Volunteer";
import Transparency from "@/pages/Transparency";
import Contact from "@/pages/Contact";
import People from "@/pages/People";
import ProjectDawn from "@/pages/dawn/ProjectDawn";
import ValleyOfRiver from "@/pages/dawn/ValleyOfRiver";
import TreePlantation from "@/pages/dawn/TreePlantation";
import ButtToBoard from "@/pages/dawn/ButtToBoard";
import HighAltitude from "@/pages/dawn/HighAltitude";
import DawnFilm from "@/pages/dawn/DawnFilm";
import LaxmiStory from "@/pages/dawn/LaxmiStory";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import Dashboard from "@/pages/admin/Dashboard";

function PublicLayout() {
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}

function TravelRedirect() {
  const { id } = useParams();
  return <Navigate to={`/exploration/${id}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  const isGoogleCallback = location.pathname === "/account" && new URLSearchParams(location.search).get("code");
  if (isGoogleCallback) {
    return <MemberAuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/focus" element={<Focus />} />
        <Route path="/programmes" element={<Programmes />} />
        <Route path="/programmes/:id" element={<ProgrammeDetail />} />
        <Route path="/where-we-work" element={<WhereWeWork />} />
        <Route path="/people" element={<People />} />
        <Route path="/dawn" element={<ProjectDawn />} />
        <Route path="/dawn/valley-of-river" element={<ValleyOfRiver />} />
        <Route path="/dawn/tree-plantation" element={<TreePlantation />} />
        <Route path="/dawn/butt-to-board" element={<ButtToBoard />} />
        <Route path="/dawn/high-altitude" element={<HighAltitude />} />
        <Route path="/dawn/film" element={<DawnFilm />} />
        <Route path="/dawn/laxmi" element={<LaxmiStory />} />
        <Route path="/impact" element={<Impact />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/stories" element={<Stories />} />
        <Route path="/stories/:id" element={<StoryDetail />} />
        <Route path="/expeditions" element={<Expeditions />} />
        <Route path="/expeditions/:id" element={<JourneyDetail />} />
        <Route path="/films" element={<Films />} />
        <Route path="/films/the-last-wild-horizon-film" element={<Navigate to="/films/the-last-horizon-film" replace />} />
        <Route path="/films/:slug" element={<FilmDetail />} />
        <Route path="/exploration" element={<Navigate to="/expeditions" replace />} />
        <Route path="/exploration/:id" element={<JourneyDetail />} />
        <Route path="/travel" element={<Navigate to="/exploration" replace />} />
        <Route path="/travel/:id" element={<TravelRedirect />} />
        <Route path="/flagship" element={<Flagship />} />
        <Route path="/learning" element={<Learning />} />
        <Route path="/learning/training" element={<TrainingCatalogue />} />
        <Route path="/learning/programme/:slug" element={<TrainingDetail />} />
        <Route path="/learning/pathways" element={<Pathways />} />
        <Route path="/learning/field" element={<FieldExperiences />} />
        <Route path="/learning/upcoming" element={<Upcoming />} />
        <Route path="/learning/institutional" element={<Institutional />} />
        <Route path="/verify" element={<Verify />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/volunteer" element={<Volunteer />} />
        <Route path="/account" element={<MemberAccount />} />
        <Route path="/last-horizon/apply" element={<LastHorizonApply />} />
        <Route path="/dawn/apply" element={<DawnApply />} />
        <Route path="/last-horizon/terms" element={<LastHorizonTerms />} />
        <Route path="/dawn/terms" element={<DawnTerms />} />
        <Route path="/transparency" element={<Transparency />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal/:doc" element={<Legal />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MemberAuthProvider>
            <SmoothScroll>
              <AppRoutes />
            </SmoothScroll>
            <Toaster position="bottom-right" theme="dark" richColors />
          </MemberAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
